/**
 * useForm.ts - Form Management Hook
 * 
 * CHANGE HISTORY:
 * - 251201_2340 [pending] danielbeer: Initial creation
 * 
 * Hook for managing complete forms with multiple fields, validation, and submission.
 */

import { useState, useCallback } from 'react';
// import { useInputr } from '@opcentral/inputr';
import type { FormConfig, FormState } from '../types';

export interface UseFormOptions {
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  onSubmit?: (values: Record<string, any>) => void | Promise<void>;
}

export interface UseFormReturn {
  values: Record<string, any>;
  errors: Record<string, string[]>;
  touched: Record<string, boolean>;
  dirty: Record<string, boolean>;
  valid: boolean;
  submitting: boolean;
  setValue: (name: string, value: any) => void;
  setTouched: (name: string, touched: boolean) => void;
  setError: (name: string, error: string) => void;
  validate: () => Promise<boolean>;
  validateField: (name: string) => Promise<boolean>;
  handleSubmit: (e?: React.FormEvent) => Promise<void>;
  reset: () => void;
}

/**
 * Hook for managing a complete form
 * 
 * @param config - Form configuration
 * @param options - Form options
 * @returns Form state and methods
 * 
 * @example
 * ```tsx
 * const form = useForm({
 *   name: 'user-form',
 *   fields: [
 *     { type: 'text', name: 'username', label: 'Username', validation: { required: true } },
 *     { type: 'email', name: 'email', label: 'Email', validation: { required: true, email: true } }
 *   ]
 * }, {
 *   onSubmit: async (values) => {
 *     await api.createUser(values);
 *   }
 * });
 * 
 * return (
 *   <form onSubmit={form.handleSubmit}>
 *     {form.config.fields.map(field => (
 *       <input
 *         key={field.name}
 *         name={field.name}
 *         value={form.values[field.name] || ''}
 *         onChange={(e) => form.setValue(field.name, e.target.value)}
 *       />
 *     ))}
 *     <button type="submit" disabled={!form.valid || form.submitting}>
 *       Submit
 *     </button>
 *   </form>
 * );
 * ```
 */
export function useForm(
  config: FormConfig,
  options: UseFormOptions = {}
): UseFormReturn {
  // const inputr = useInputr(); // TODO: Re-enable after fixing type conflicts
  
  // Initialize form state
  const [formState, setFormState] = useState<FormState>(() => {
    const initialValues: Record<string, any> = {};
    const initialErrors: Record<string, string[]> = {};
    const initialTouched: Record<string, boolean> = {};
    const initialDirty: Record<string, boolean> = {};
    
    config.fields?.forEach(field => {
      initialValues[field.name] = field.value || field.defaultValue || '';
      initialErrors[field.name] = [];
      initialTouched[field.name] = false;
      initialDirty[field.name] = false;
    });
    
    return {
      values: initialValues,
      errors: initialErrors,
      touched: initialTouched,
      dirty: initialDirty,
      isValid: true,
      isSubmitting: false,
      submitCount: 0,
    };
  });

  const [submitting, setSubmitting] = useState(false);

  // Create form in inputr
  // TODO: Fix type conflicts between Formr and Inputr FormConfig types
  // useEffect(() => {
  //   inputr.createForm(config);
  // }, [inputr, config]);

  // Set field value
  const setValue = useCallback((name: string, value: any) => {
    setFormState(prev => ({
      ...prev,
      values: { ...prev.values, [name]: value },
      dirty: { ...prev.dirty, [name]: true },
    }));
    
    const field = config.fields?.find(f => f.name === name);
    if (field?.onChange) {
      field.onChange(value);
    }
    
    if (options.validateOnChange) {
      validateField(name);
    }
  }, [config.fields, options.validateOnChange]);

  // Set field touched
  const setTouched = useCallback((name: string, touched: boolean) => {
    setFormState(prev => ({
      ...prev,
      touched: { ...prev.touched, [name]: touched },
    }));
    
    if (touched && options.validateOnBlur) {
      validateField(name);
    }
  }, [options.validateOnBlur]);

  // Set field error
  const setError = useCallback((name: string, error: string) => {
    setFormState(prev => ({
      ...prev,
      errors: { ...prev.errors, [name]: [error] },
    }));
  }, []);

  // Validate single field
  const validateField = useCallback(async (name: string): Promise<boolean> => {
    const field = config.fields?.find(f => f.name === name);
    if (!field) return true;
    
    // TODO: Re-enable inputr validation after fixing type conflicts
    // const value = formState.values[name];
    // const result = await inputr.validateInput(field, value);
    
    // setFormState(prev => ({
    //   ...prev,
    //   errors: { ...prev.errors, [name]: result.errors },
    // }));
    
    return true; // result.valid;
  }, [config.fields, formState.values]);

  // Validate entire form
  const validate = useCallback(async (): Promise<boolean> => {
    if (!config.fields) return true;
    
    const validationResults = await Promise.all(
      config.fields.map(field => validateField(field.name))
    );
    
    const isValid = validationResults.every(result => result);
    
    setFormState(prev => ({
      ...prev,
      isValid: isValid,
    }));
    
    return isValid;
  }, [config.fields, validateField]);

  // Handle form submission
  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    setSubmitting(true);
    
    try {
      const isValid = await validate();
      
      if (isValid && options.onSubmit) {
        await options.onSubmit(formState.values);
        
        setFormState(prev => ({
          ...prev,
          submitted: true,
        }));
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
    }
  }, [validate, options, formState.values]);

  // Reset form
  const reset = useCallback(() => {
    const initialValues: Record<string, any> = {};
    const initialErrors: Record<string, string[]> = {};
    const initialTouched: Record<string, boolean> = {};
    const initialDirty: Record<string, boolean> = {};
    
    config.fields?.forEach(field => {
      initialValues[field.name] = field.value || field.defaultValue || '';
      initialErrors[field.name] = [];
      initialTouched[field.name] = false;
      initialDirty[field.name] = false;
    });
    
    setFormState({
      values: initialValues,
      errors: initialErrors,
      touched: initialTouched,
      dirty: initialDirty,
      isValid: true,
      isSubmitting: false,
      submitCount: 0,
    });
  }, [config.fields]);

  return {
    values: formState.values,
    errors: formState.errors,
    touched: formState.touched,
    dirty: formState.dirty,
    valid: formState.isValid,
    submitting,
    setValue,
    setTouched,
    setError,
    validate,
    validateField,
    handleSubmit,
    reset,
  };
}

export default useForm;
